import { useEffect } from 'react';
import { usePickers } from '../../hooks/usePickers';
import { useDrivers } from '../../hooks/useDrivers';
import type { TimelineEntry } from '../../hooks/useTimeline';
import {
    ORDER_STATUS,
    STATUS_ICONS,
    STATUS_ICONS_DEFAULT,
    STATUS_DESCRIPTIONS,
    STATUS_LABELS,
    getPaymentStatusBadgeColor,
} from '../../constants/orderStatuses';

interface StatusesLogTabProps {
    orderId:  string;
    timeline: TimelineEntry[];
    loading:  boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function getOrderStatusDescription(statusName: string, index: number, allEntries: TimelineEntry[], modelId?: string | null): string {
    const orderBefore = allEntries.slice(0, index).filter(e => e.type === 'order');
    if (statusName === ORDER_STATUS.ASSIGN_DRIVER && orderBefore.some(e => e.type === 'order' && e.statusName === ORDER_STATUS.DRIVER_ASSIGNED)) {
        return 'Driver assignment was revoked. Order has been returned to the dispatch pool for re-assignment.';
    }
    if (statusName === ORDER_STATUS.PACKER_ASSIGNED && orderBefore.some(e => e.type === 'order' && e.statusName === ORDER_STATUS.PACKER_ASSIGNED)) {
        return 'The previous packer was unassigned. A new warehouse member has been assigned to prepare this order.';
    }
    // AWAITING_PACKING with a picker stamped = picker was unassigned
    if (statusName === ORDER_STATUS.AWAITING_PACKING && modelId) {
        return 'Picker was unassigned. Order has been returned to the packing queue.';
    }
    return STATUS_DESCRIPTIONS[statusName] ?? 'Status update recorded.';
}

function formatStatusLabel(status: string | null): string {
    if (!status) return '—';
    return STATUS_LABELS[status] ?? status.replace(/_/g, ' ').toLowerCase();
}

function paymentDotColor(status: string | null): string {
    if (!status) return 'bg-stone-300';
    const s = status.toUpperCase();
    if (s.includes('SUCCEEDED') || s === 'SUCCESS') return 'bg-emerald-500';
    if (s.includes('FAILED') || s.includes('DECLINED') || s.includes('CANCELLED') || s.includes('VOIDED') || s === 'CAPTURE_FAILED') return 'bg-rose-500';
    if (s.includes('PRE_AUTHORIZED') || s.includes('PROCESSING')) return 'bg-sky-500';
    return 'bg-amber-400';
}

function deliveryDotColor(statusName: string): string {
    if (statusName === 'DELIVERY_COMPLETE') return 'bg-emerald-500';
    if (statusName === 'DELIVERY_FAILED')   return 'bg-rose-500';
    if (statusName === 'ON_ROUTE')          return 'bg-lime-500';
    return 'bg-sky-500';
}

// ── Timestamp ──────────────────────────────────────────────────────────────

function Timestamp({ value }: { value: string }) {
    return (
        <div className="text-right shrink-0">
            <div className="text-xs text-stone-600 font-medium">
                {new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="text-xs text-stone-500 mt-1">
                {new Date(value).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
            </div>
        </div>
    );
}

// ── Entry cards ────────────────────────────────────────────────────────────

function OrderEntryCard({ entry, index, allEntries, isFirst, isLast, actorName }: {
    entry: Extract<TimelineEntry, { type: 'order' }>;
    index: number;
    allEntries: TimelineEntry[];
    isFirst: boolean;
    isLast: boolean;
    actorName: string | null;
}) {
    const colors             = STATUS_ICONS[entry.statusName] ?? STATUS_ICONS_DEFAULT;
    const isDriver           = entry.model_type === 'Driver';
    const orderBefore        = allEntries.slice(0, index).filter(e => e.type === 'order');
    const isDriverRevoked    = entry.statusName === ORDER_STATUS.ASSIGN_DRIVER    && orderBefore.some(e => e.type === 'order' && e.statusName === ORDER_STATUS.DRIVER_ASSIGNED);
    const isPackerReassigned = entry.statusName === ORDER_STATUS.PACKER_ASSIGNED  && orderBefore.some(e => e.type === 'order' && e.statusName === ORDER_STATUS.PACKER_ASSIGNED);
    const isPickerUnassigned = entry.statusName === ORDER_STATUS.AWAITING_PACKING && !!entry.model_id;

    return (
        <div className="flex gap-6">
            <div className="flex flex-col items-center">
                <div className={`relative z-10 w-12 h-12 rounded-full ring-4 ${colors.ring} ${colors.dot} flex items-center justify-center text-white font-semibold text-lg shadow-lg transition-transform hover:scale-110`}>
                    {colors.icon}
                </div>
                {!isLast && <div className={`w-1 h-16 ${colors.dot} mt-2 opacity-30`} />}
            </div>
            <div className={`flex-1 ${!isLast ? 'pb-8' : 'pb-0'}`}>
                <div className={`rounded-lg border-2 ${colors.ring} ${colors.bg} p-4 transition-all hover:shadow-md`}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1">
                            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-tight flex items-center gap-2">
                                {entry.statusName.replace(/_/g, ' ') || 'UNKNOWN'}
                                {isDriverRevoked    && <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] rounded-md border border-orange-200">REVOKED</span>}
                                {isPackerReassigned && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-md border border-amber-200">RE-ASSIGNED</span>}
                                {isPickerUnassigned && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] rounded-md border border-red-200">PICKER UNASSIGNED</span>}
                            </h4>
                            {actorName && (
                                <div className="mt-1.5 flex items-center gap-1.5">
                                    <span className="text-[10px] font-bold uppercase text-stone-400">{isDriver ? 'Driver' : 'Picker'}:</span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ring-1 ring-inset ${isDriver ? 'bg-sky-100 text-sky-700 ring-sky-700/10' : 'bg-pink-100 text-pink-700 ring-pink-700/10'}`}>
                                        {actorName}
                                    </span>
                                </div>
                            )}
                            <div className="flex gap-2 mt-2">
                                {isFirst && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">Started</span>}
                                {isLast  && <span className="px-2 py-1 bg-[#f0f7e6] text-[#5a7f28] text-xs font-semibold rounded-full">Current State</span>}
                            </div>
                        </div>
                        <Timestamp value={entry.created_at} />
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                        {getOrderStatusDescription(entry.statusName, index, allEntries, entry.model_id)}
                    </p>
                </div>
            </div>
        </div>
    );
}

function PaymentEntryCard({ entry, isLast }: {
    entry: Extract<TimelineEntry, { type: 'payment' }>;
    isLast: boolean;
}) {
    const dotColor = paymentDotColor(entry.current_status);

    return (
        <div className="flex gap-6">
            <div className="flex flex-col items-center">
                <div className={`relative z-10 w-12 h-12 rounded-full ring-4 ring-white ${dotColor} flex items-center justify-center text-white font-semibold text-lg shadow-lg transition-transform hover:scale-110`}>
                    💳
                </div>
                {!isLast && <div className={`w-1 h-16 ${dotColor} mt-2 opacity-30`} />}
            </div>
            <div className={`flex-1 ${!isLast ? 'pb-8' : 'pb-0'}`}>
                <div className="rounded-lg border-2 border-stone-200 bg-stone-50 p-4 transition-all hover:shadow-md">
                    <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1">
                            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-tight mb-2">
                                Payment Update
                            </h4>
                            <div className="flex flex-wrap items-center gap-2">
                                {entry.previous_status ? (
                                    <>
                                        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${getPaymentStatusBadgeColor(entry.previous_status)}`}>
                                            {formatStatusLabel(entry.previous_status)}
                                        </span>
                                        <svg className="h-3 w-3 text-stone-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </>
                                ) : (
                                    <span className="text-xs text-stone-400 italic">Initial state</span>
                                )}
                                <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${getPaymentStatusBadgeColor(entry.current_status ?? '')}`}>
                                    {formatStatusLabel(entry.current_status)}
                                </span>
                            </div>
                        </div>
                        <Timestamp value={entry.created_at} />
                    </div>
                    {entry.reason && (
                        <p className="text-xs text-stone-500 italic mt-1">"{entry.reason}"</p>
                    )}
                </div>
            </div>
        </div>
    );
}

function DeliveryEntryCard({ entry, isLast, driverName, allEntries, index }: {
    entry: Extract<TimelineEntry, { type: 'delivery' }>;
    isLast: boolean;
    driverName: string | null;
    allEntries: TimelineEntry[];
    index: number;
}) {
    const dotColor = deliveryDotColor(entry.statusName);
    const icon = entry.statusName === 'DELIVERY_COMPLETE' ? '✓'
        : entry.statusName === 'DELIVERY_FAILED'   ? '⚠'
            : entry.statusName === 'ON_ROUTE'          ? '🚚'
                : '👤';

    // ASSIGN_DRIVER with a driver_id = revoked assignment
    const isRevoked = entry.statusName === 'ASSIGN_DRIVER' && !!entry.driver_id;
    // DRIVER_ASSIGNED that appears more than once = reassignment
    const prevDeliveryEntries = allEntries.slice(0, index).filter(
        e => e.type === 'delivery' && e.statusName === 'DRIVER_ASSIGNED'
    );
    const isReassigned = entry.statusName === 'DRIVER_ASSIGNED' && prevDeliveryEntries.length > 0;

    return (
        <div className="flex gap-6">
            <div className="flex flex-col items-center">
                <div className={`relative z-10 w-12 h-12 rounded-full ring-4 ring-white ${dotColor} flex items-center justify-center text-white font-semibold text-lg shadow-lg transition-transform hover:scale-110`}>
                    {icon}
                </div>
                {!isLast && <div className={`w-1 h-16 ${dotColor} mt-2 opacity-30`} />}
            </div>
            <div className={`flex-1 ${!isLast ? 'pb-8' : 'pb-0'}`}>
                <div className={`rounded-lg border-2 p-4 transition-all hover:shadow-md ${isRevoked ? 'border-orange-200 bg-orange-50' : 'border-sky-200 bg-sky-50'}`}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1">
                            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-tight flex items-center gap-2">
                                {entry.statusName.replace(/_/g, ' ')}
                                {isRevoked    && <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] rounded-md border border-orange-200">REVOKED</span>}
                                {isReassigned && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-md border border-amber-200">REASSIGNED</span>}
                            </h4>
                            {driverName && (
                                <div className="mt-1.5 flex items-center gap-1.5">
                                    <span className="text-[10px] font-bold uppercase text-stone-400">Driver:</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ring-1 ring-inset bg-sky-100 text-sky-700 ring-sky-700/10">
                                        {driverName}
                                    </span>
                                </div>
                            )}
                            <div className="flex gap-2 mt-2">
                                {isLast && <span className="px-2 py-1 bg-[#f0f7e6] text-[#5a7f28] text-xs font-semibold rounded-full">Current State</span>}
                            </div>
                        </div>
                        <Timestamp value={entry.created_at} />
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                        {isRevoked
                            ? 'Driver assignment was revoked. Order has been returned to the dispatch pool.'
                            : STATUS_DESCRIPTIONS[entry.statusName] ?? 'Delivery status updated.'}
                    </p>
                </div>
            </div>
        </div>
    );
}

// ── Main component ─────────────────────────────────────────────────────────

export const StatusesLogTab = ({ orderId, timeline, loading }: StatusesLogTabProps) => {
    const { pickers, fetchPickers } = usePickers();
    const { drivers, fetchDrivers } = useDrivers();

    useEffect(() => {
        if (pickers.length === 0) void fetchPickers();
        if (drivers.length === 0) void fetchDrivers();
    }, [orderId, pickers.length, drivers.length, fetchPickers, fetchDrivers]);

    const getActorName = (modelId: string | null, modelType: string | null): string | null => {
        if (!modelId || !modelType) return null;
        if (modelType === 'Picker') {
            const p = pickers.find(p => String(p.id) === String(modelId));
            return p ? `${p.first_name} ${p.last_name}` : 'Unknown Picker';
        }
        if (modelType === 'Driver') {
            const d = drivers.find(d => String(d.auth_id) === String(modelId));
            return d ? d.driver_name : 'Unknown Driver';
        }
        return null;
    };

    const getDriverName = (driverId: string | null): string | null => {
        if (!driverId) return null;
        const d = drivers.find(d => String(d.auth_id) === String(driverId) || String(d.id) === String(driverId));
        return d ? d.driver_name : null;
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-stone-200 border-t-[#ee2b74] rounded-full animate-spin" />
                <p className="mt-4 text-sm text-stone-600">Loading order timeline…</p>
            </div>
        );
    }

    if (timeline.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <p className="text-stone-500">No status updates yet</p>
            </div>
        );
    }

    const orderCount    = timeline.filter(e => e.type === 'order').length;
    const paymentCount  = timeline.filter(e => e.type === 'payment').length;
    const deliveryCount = timeline.filter(e => e.type === 'delivery').length;

    return (
        <div className="relative">
            <div className="space-y-0">
                {timeline.map((entry, index) => {
                    const isFirst = index === 0;
                    const isLast  = index === timeline.length - 1;

                    if (entry.type === 'order') {
                        return (
                            <OrderEntryCard
                                key={entry.id}
                                entry={entry}
                                index={index}
                                allEntries={timeline}
                                isFirst={isFirst}
                                isLast={isLast}
                                actorName={getActorName(entry.model_id, entry.model_type)}
                            />
                        );
                    }
                    if (entry.type === 'payment') {
                        return (
                            <PaymentEntryCard
                                key={entry.id}
                                entry={entry}
                                isLast={isLast}
                            />
                        );
                    }
                    return (
                        <DeliveryEntryCard
                            key={entry.id}
                            entry={entry}
                            isLast={isLast}
                            driverName={getDriverName(entry.driver_id)}
                            allEntries={timeline}
                            index={index}
                        />
                    );
                })}
            </div>

            <div className="mt-8 pt-6 border-t-2 border-stone-200">
                <div className="grid grid-cols-4 gap-4">
                    <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                        <p className="text-xs text-blue-600 font-medium uppercase tracking-wide mb-1">Total Events</p>
                        <p className="text-2xl font-bold text-blue-900">{timeline.length}</p>
                    </div>
                    <div className="rounded-lg bg-purple-50 border border-purple-200 p-4">
                        <p className="text-xs text-purple-600 font-medium uppercase tracking-wide mb-1">Order Steps</p>
                        <p className="text-2xl font-bold text-purple-900">{orderCount}</p>
                    </div>
                    <div className="rounded-lg bg-stone-50 border border-stone-200 p-4">
                        <p className="text-xs text-stone-600 font-medium uppercase tracking-wide mb-1">Payment Events</p>
                        <p className="text-2xl font-bold text-stone-900">{paymentCount}</p>
                    </div>
                    <div className="rounded-lg bg-sky-50 border border-sky-200 p-4">
                        <p className="text-xs text-sky-600 font-medium uppercase tracking-wide mb-1">Delivery Events</p>
                        <p className="text-2xl font-bold text-sky-900">{deliveryCount}</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                        <p className="text-xs text-amber-600 font-medium uppercase tracking-wide mb-1">Started</p>
                        <p className="text-sm text-amber-900 font-medium">
                            {new Date(timeline[0]?.created_at).toLocaleDateString()}
                        </p>
                    </div>
                    <div className="rounded-lg bg-[#f0f7e6] border border-[#c5e08a] p-4">
                        <p className="text-xs text-[#5a7f28] font-medium uppercase tracking-wide mb-1">Last Updated</p>
                        <p className="text-sm text-[#5a7f28] font-medium">
                            {new Date(timeline[timeline.length - 1]?.created_at).toLocaleDateString()}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatusesLogTab;